/**
 * "Color by value": a text takes the colour of the range its number falls in,
 * e.g. 1-2 red, 2-3 amber, 3-4 green - as many ranges as the designer adds.
 *
 * Ranges are checked in the order listed; the first one that contains the
 * number wins (From and To both included, an empty side = no limit), so
 * touching ranges (1-2, 2-3) resolve to the earlier one at the boundary.
 */
import type { ValueColorRange } from '../design-builder.types';

/** The colour for `value`, or null (no ranges, no number, or no range matches). */
/**
 * Ranges whose From / To point at markers (other texts) - their live numbers
 * filled in. A range whose marker has no number yet is left out (no guessing).
 */
export function resolveValueColorRanges(
  ranges: ReadonlyArray<ValueColorRange> | null | undefined,
  markerNumber: (id: string) => number | null,
): ValueColorRange[] {
  const out: ValueColorRange[] = [];
  (ranges ?? []).forEach((range) => {
    const from = range.fromId ? markerNumber(range.fromId) : range.from;
    const to = range.toId ? markerNumber(range.toId) : range.to;
    if ((range.fromId && from === null) || (range.toId && to === null)) return;
    out.push({ color: range.color, from, to });
  });
  return out;
}

export function valueColorFor(ranges: ReadonlyArray<ValueColorRange> | null | undefined, value: number | null): string | null {
  if (!ranges?.length || value === null || !Number.isFinite(value)) return null;
  const match = ranges.find((range) =>
    !!range.color
    && (range.from === null || range.from === undefined || value >= range.from)
    && (range.to === null || range.to === undefined || value <= range.to));
  return match?.color ?? null;
}

/**
 * A number out of a live value: numbers as they are, text such as "$1,234.5",
 * "6.6%" or "(12)" read as its first number. Null when there is none.
 */
export function numericValue(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (value === null || value === undefined) return null;
  const match = String(value).replace(/,/g, '').match(/-?\d+(?:\.\d+)?/);
  if (!match) return null;
  const parsed = Number(match[0]);
  return Number.isFinite(parsed) ? parsed : null;
}
